import { faro, LogLevel as FaroLogLevel } from '@grafana/faro-web-sdk';
import { getRawTraceId } from './tracing';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const FARO_LEVEL_MAP: Record<LogLevel, FaroLogLevel> = {
  debug: FaroLogLevel.DEBUG,
  info: FaroLogLevel.INFO,
  warn: FaroLogLevel.WARN,
  error: FaroLogLevel.ERROR,
};

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  namespace: string | null;
  message: string;
  data: unknown | null;
  traceId: string | null;
}

interface NamespacePattern {
  regex: RegExp;
  negated: boolean;
}

function serializeArg(value: unknown): string {
  if (value instanceof Error) {
    return JSON.stringify({ name: value.name, message: value.message, stack: value.stack });
  }
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  return String(value as string | number | boolean);
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000;
  private messageThrottle: Map<string, { count: number; lastLogged: number }> = new Map();
  private readonly throttleWindow = 10000;
  private readonly maxRepeats = 3;
  private configuredLevel: LogLevel | null = null;
  private configuredFaroLevel: LogLevel | null = null;
  private throttleCleanupTimer?: ReturnType<typeof setInterval>;

  private namespacePatterns: NamespacePattern[] = [];
  private namespacePatternString = '*';
  private readonly seenNamespaces = new Set<string>();

  private readonly sessionId: string;
  private userId: string | null = null;
  private deviceId: string | null = null;

  private readLevelFromStorage(storageKey: string): LogLevel | null {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(storageKey);
    if (stored !== null && stored in LOG_LEVEL_PRIORITY) {
      return stored as LogLevel;
    }
    return null;
  }

  private getConfiguredLevel(): LogLevel {
    if (this.configuredLevel !== null) return this.configuredLevel;
    const stored = this.readLevelFromStorage('aircast_log_level');
    this.configuredLevel = stored !== null ? stored : 'warn';
    return this.configuredLevel;
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this.getConfiguredLevel()];
  }

  private getConfiguredFaroLevel(): LogLevel {
    if (this.configuredFaroLevel !== null) return this.configuredFaroLevel;
    const stored = this.readLevelFromStorage('aircast_faro_log_level');
    this.configuredFaroLevel = stored !== null ? stored : 'debug';
    return this.configuredFaroLevel;
  }

  private shouldShipToFaro(level: LogLevel): boolean {
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this.getConfiguredFaroLevel()];
  }

  getLogLevel(): LogLevel {
    return this.getConfiguredLevel();
  }

  setLogLevel(level: LogLevel) {
    if (level in LOG_LEVEL_PRIORITY) {
      const previousLevel = this.configuredLevel;
      this.configuredLevel = level;
      if (typeof window !== 'undefined') {
        localStorage.setItem('aircast_log_level', level);
      }
      console.info(`[Logger] Log level changed: ${previousLevel ?? 'default'} → ${level}`);
    } else {
      console.warn(`[Logger] Invalid log level: ${level}. Valid levels: debug, info, warn, error`);
    }
  }

  getFaroLogLevel(): LogLevel {
    return this.getConfiguredFaroLevel();
  }

  setFaroLogLevel(level: LogLevel) {
    if (level in LOG_LEVEL_PRIORITY) {
      const previous = this.configuredFaroLevel;
      this.configuredFaroLevel = level;
      if (typeof window !== 'undefined') {
        localStorage.setItem('aircast_faro_log_level', level);
      }
      console.info(`[Logger] Faro log level changed: ${previous ?? 'default'} → ${level}`);
    } else {
      console.warn(`[Logger] Invalid Faro log level: ${level}. Valid levels: debug, info, warn, error`);
    }
  }

  private parseNamespacePatterns(pattern: string): NamespacePattern[] {
    if (!pattern || pattern.trim() === '') return [];

    return pattern
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => {
        const negated = p.startsWith('-');
        const raw = negated ? p.slice(1) : p;
        const escaped = raw.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
        return { regex: new RegExp(`^${escaped}$`), negated };
      });
  }

  private isNamespaceEnabled(namespace: string | undefined): boolean {
    if (!namespace) return true;

    const patterns = this.namespacePatterns;
    if (patterns.length === 0) return false;

    for (const p of patterns) {
      if (p.negated && p.regex.test(namespace)) return false;
    }

    for (const p of patterns) {
      if (!p.negated && p.regex.test(namespace)) return true;
    }

    return false;
  }

  enable(pattern: string) {
    this.namespacePatternString = pattern;
    this.namespacePatterns = this.parseNamespacePatterns(pattern);
    if (typeof window !== 'undefined') {
      localStorage.setItem('aircast_log_namespaces', pattern);
    }
    console.info(`[Logger] Namespaces enabled: ${pattern}`);
  }

  disable(namespace: string) {
    const current = this.namespacePatternString;
    const newPattern = current ? `${current},-${namespace}` : `-${namespace}`;
    this.enable(newPattern);
  }

  getEnabledNamespaces(): string {
    return this.namespacePatternString;
  }

  listNamespaces(): string[] {
    return Array.from(this.seenNamespaces).sort();
  }

  constructor() {
    this.sessionId = this.generateSessionId();

    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('aircast_log_namespaces');
      if (stored) {
        this.namespacePatternString = stored;
      }
    }
    this.namespacePatterns = this.parseNamespacePatterns(this.namespacePatternString);

    if (typeof window !== 'undefined') {
      this.throttleCleanupTimer = setInterval(() => {
        this.cleanupThrottleMap();
      }, 30000);
    }
  }

  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  getSessionId(): string {
    return this.sessionId;
  }

  setContext(context: { userId?: string | null; deviceId?: string | null }) {
    if ('userId' in context) {
      this.userId = context.userId ?? null;
    }
    if ('deviceId' in context) {
      this.deviceId = context.deviceId ?? null;
    }
  }

  getContext(): { sessionId: string; userId: string | null; deviceId: string | null } {
    return {
      sessionId: this.sessionId,
      userId: this.userId,
      deviceId: this.deviceId,
    };
  }

  private cleanupThrottleMap() {
    const now = Date.now();
    for (const [key, value] of this.messageThrottle.entries()) {
      if (now - value.lastLogged > this.throttleWindow * 2) {
        this.messageThrottle.delete(key);
      }
    }
  }

  private readonly sensitiveKeys = new Set([
    'password',
    'token',
    'secret',
    'authorization',
    'auth_token',
    'access_token',
    'refresh_token',
    'session_token',
    'api_key',
    'apikey',
    'credential',
    'private_key',
    'privatekey',
  ]);

  private sanitize(data: unknown, depth = 0): unknown {
    if (depth > 10) return '[MAX_DEPTH]';

    if (data === null || data === undefined) return data;

    if (typeof data === 'string') {
      if (data.startsWith('eyJ') && data.includes('.')) {
        return '[REDACTED_JWT]';
      }
      if (data.length > 40 && /^[A-Za-z0-9+/=_-]+$/.test(data)) {
        return '[REDACTED_TOKEN]';
      }
      return data;
    }

    if (Array.isArray(data)) {
      return data.map((item) => this.sanitize(item, depth + 1));
    }

    if (data instanceof Error) {
      return { name: data.name, message: data.message, stack: data.stack };
    }

    if (typeof data === 'object') {
      const sanitized: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
        const lowerKey = key.toLowerCase();
        if (this.sensitiveKeys.has(lowerKey)) {
          sanitized[key] = '[REDACTED]';
        } else {
          sanitized[key] = this.sanitize(value, depth + 1);
        }
      }
      return sanitized;
    }

    return data;
  }

  private flattenToContext(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      if (value === null || value === undefined) {
        continue;
      } else if (value instanceof Error) {
        result[fullKey] = serializeArg(value);
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        Object.assign(result, this.flattenToContext(value as Record<string, unknown>, fullKey));
      } else {
        result[fullKey] = serializeArg(value);
      }
    }
    return result;
  }

  private sendToFaro(level: LogLevel, message: string, data: unknown | null, traceId: string | null, namespace?: string) {
    if (!faro?.api || !this.shouldShipToFaro(level)) {
      return;
    }

    try {
      const context: Record<string, string> = {
        session_id: this.sessionId,
        platform: 'web',
      };

      if (namespace) {
        context.namespace = namespace;
      }
      if (this.userId) {
        context.user_id = this.userId;
      }
      if (this.deviceId) {
        context.device_id = this.deviceId;
      }
      if (traceId) {
        context.trace_id = traceId;
      }

      if (data !== null && typeof data === 'object' && !Array.isArray(data)) {
        Object.assign(context, this.flattenToContext(data as Record<string, unknown>));
      } else if (data !== null) {
        context.data = serializeArg(data);
      }

      faro.api.pushLog([message], {
        level: FARO_LEVEL_MAP[level],
        context,
      });
    } catch {
    }
  }

  private emit(level: LogLevel, message: string, data?: unknown, namespace?: string) {
    const now = Date.now();
    const sanitizedData = data !== undefined ? this.sanitize(data) : null;

    if (namespace) {
      this.seenNamespaces.add(namespace);
    }

    const traceId = getRawTraceId();

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      namespace: namespace ?? null,
      message,
      data: sanitizedData,
      traceId,
    };

    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    this.sendToFaro(level, message, sanitizedData, traceId, namespace);

    const messageKey = `${level}:${message}`;
    const throttleData = this.messageThrottle.get(messageKey);

    let isThrottled = false;
    if (throttleData) {
      if (now - throttleData.lastLogged > this.throttleWindow) {
        throttleData.count = 1;
        throttleData.lastLogged = now;
      } else {
        throttleData.count++;
        if (throttleData.count > this.maxRepeats) {
          isThrottled = true;
        }
      }
    } else {
      this.messageThrottle.set(messageKey, { count: 1, lastLogged: now });
    }

    if (!this.shouldLog(level) || isThrottled || !this.isNamespaceEnabled(namespace)) {
      return;
    }

    if (import.meta.env.DEV) {
      if (sanitizedData !== null) {
        console[level](message, sanitizedData);
      } else {
        console[level](message);
      }
    } else {
      if (sanitizedData !== null) {
        console[level](`[${entry.timestamp}] ${message}`, sanitizedData);
      } else {
        console[level](`[${entry.timestamp}] ${message}`);
      }
    }
  }

  debug(message: string, data?: unknown) {
    this.emit('debug', message, data);
  }

  info(message: string, data?: unknown) {
    this.emit('info', message, data);
  }

  warn(message: string, data?: unknown) {
    this.emit('warn', message, data);
  }

  error(message: string, data?: unknown) {
    this.emit('error', message, data);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  clearLogs() {
    this.logs = [];
  }

  captureError(error: Error, context?: Record<string, unknown>) {
    this.error(error.message, {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      context,
    });
  }

  destroy() {
    if (this.throttleCleanupTimer) {
      clearInterval(this.throttleCleanupTimer);
    }
  }
}

export const logger = new Logger();

type LoggerInternal = { emit(level: LogLevel, message: string, data?: unknown, namespace?: string): void };
const internal = logger as unknown as LoggerInternal;
if (typeof internal.emit !== 'function') {
  throw new Error('Logger.emit method not found — did you rename it? Update LoggerInternal type.');
}

if (typeof window !== 'undefined') {
  (window as Window & { __AIRCAST_LOGGER__?: Logger }).__AIRCAST_LOGGER__ = logger;
}

export interface NamespacedLogger {
  debug(message: string, data?: unknown): void;
  info(message: string, data?: unknown): void;
  warn(message: string, data?: unknown): void;
  error(message: string, data?: unknown): void;
}

export function createLogger(namespace: string): NamespacedLogger {
  const prefix = `[${namespace}]`;
  return {
    debug: (message: string, data?: unknown) => internal.emit('debug', `${prefix} ${message}`, data, namespace),
    info: (message: string, data?: unknown) => internal.emit('info', `${prefix} ${message}`, data, namespace),
    warn: (message: string, data?: unknown) => internal.emit('warn', `${prefix} ${message}`, data, namespace),
    error: (message: string, data?: unknown) => internal.emit('error', `${prefix} ${message}`, data, namespace),
  };
}
